import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Eye, EyeOff, ArrowRight, Upload, Building, User, Globe, Image, Lock, ImageIcon, Check, Sparkles, Box } from 'lucide-react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import countries from '@/components/Countries';
import AuthService from '@/api/auth.service';
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import LogoWithSlogan from "../../public/images/logo_with_slogan.png";
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { motion, AnimatePresence } from "framer-motion";
import { useBrand } from '@/lib/brand';

type FormSchema = z.ZodObject<{
  username: z.ZodString;
  firstName: z.ZodString;
  lastName: z.ZodString;
  emailAddress: z.ZodString;
  phoneNumber: z.ZodString;
  gender: z.ZodOptional<z.ZodString>;
  city: z.ZodString;
  // Include other fields as needed
  [k: string]: z.ZodTypeAny;
}>;

const formSchema: FormSchema = z.object({
  username: z.string().min(3, {
    message: "Username must be at least 3 characters.",
  }),
  firstName: z.string().min(1, {
    message: "First name is required.",
  }),
  lastName: z.string().min(1, {
    message: "Last name is required.",
  }),
  emailAddress: z.string().email({
    message: "Invalid email address.",
  }),
  phoneNumber: z.string().min(1, {
    message: "Phone number is required.",
  }),
  gender: z.string().optional(),
  city: z.string().min(1, {
    message: "City is required.",
  }),
  physicalAddress: z.string().min(1, {
    message: "Physical address is required.",
  }),
  country: z.string().min(1, {
    message: "Country is required.",
  }),
  password: z.string().min(6, {
    message: "Password must be at least 6 characters.",
  }),
  confirmPassword: z.string().min(6, {
    message: "Confirm Password must be at least 6 characters.",
  }).refine(data => data === formSchema.shape.password.parse(undefined), {
    message: "Passwords do not match.",
  }),
  businessName: z.string().min(1, {
    message: "Business name is required.",
  }),
  businessType: z.string().min(1, {
    message: "Business type is required.",
  }),
  businessAddress: z.string().optional(),
  businessCity: z.string().min(1, {
    message: "Business city is required.",
  }),
  businessState: z.string().optional(),
  businessCountry: z.string().min(1, {
    message: "Business country is required.",
  }),
  businessPostalCode: z.string().optional(),
  businessPhoneNumber: z.string().optional(),
  businessEmail: z.string().email({
    message: "Invalid business email address.",
  }).optional(),
  businessDescription: z.string().optional(),
  businessWebsite: z.string().url({
    message: "Please enter a valid URL.",
  }).optional().or(z.literal('')),
  businessLogoImgLink: z.string().optional(),
  businessBannerImgLink: z.string().optional(),
});

export default function Signup() {
  const brand = useBrand();
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [businessLogoImg, setBusinessLogoImg] = useState<File | null>(null);
  const [businessBannerImg, setBusinessBannerImg] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState("personal");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [businessLogoPreview, setBusinessLogoPreview] = useState<string | null>(null);
  const [businessBannerPreview, setBusinessBannerPreview] = useState<string | null>(null);

  const { toast } = useToast();

  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      firstName: "",
      lastName: "",
      emailAddress: "",
      phoneNumber: "",
      gender: "",
      city: "",
      physicalAddress: "",
      country: "",
      password: "",
      confirmPassword: "",
      businessName: "",
      businessType: "",
      businessAddress: "",
      businessCity: "",
      businessState: "",
      businessCountry: "",
      businessPostalCode: "",
      businessPhoneNumber: "",
      businessEmail: "",
      businessDescription: "",
      businessWebsite: "",
      businessLogoImgLink: "",
      businessBannerImgLink: "",
    },
    mode: "onChange",
  });

  const handleUpload = (imageType: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
      
      if (!validImageTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a valid image file (JPEG, PNG, GIF, SVG)",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: "File too large",
          description: "Image must be less than 5MB",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }

      if (imageType === "businessLogoImg") {
        setBusinessLogoImg(file);
        const reader = new FileReader();
        reader.onload = () => {
          setBusinessLogoPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else if (imageType === "businessBannerImg") {
        setBusinessBannerImg(file);
        const reader = new FileReader();
        reader.onload = () => {
          setBusinessBannerPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const calculatePasswordStrength = (password: string) => {
    if (!password) return 0;
    
    let strength = 0;
    
    // Length check
    if (password.length >= 8) strength += 1;
    
    // Contains lowercase
    if (/[a-z]/.test(password)) strength += 1;
    
    // Contains uppercase
    if (/[A-Z]/.test(password)) strength += 1;
    
    // Contains number
    if (/[0-9]/.test(password)) strength += 1;
    
    // Contains special character
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    
    return (strength / 5) * 100;
  };

  useEffect(() => {
    const password = form.watch("password");
    setPasswordStrength(calculatePasswordStrength(password));
  }, [form.watch("password")]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (data.password !== data.confirmPassword) {
      showToastWithTimeout("error", "Password Error", "Passwords do not match", 3000);
      return;
    }
    
    setIsSigningUp(true);
    const newFormData = new FormData();
    (Object.keys(data) as Array<keyof typeof data>).forEach((key: any) => {
      if (data[key] !== undefined && data[key] !== null && data[key] !== "") {
        newFormData.append(key, data[key] as string);
      }
    });

    if (businessLogoImg) {
      newFormData.append('businessLogoImg', businessLogoImg);
    }
    if (businessBannerImg) {
      newFormData.append('businessBannerImg', businessBannerImg);
    }

    try {
      const response = await AuthService.signUp(newFormData, progressEvent => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setProgress(percentCompleted);
      });
      if (response.data.result === "success") {
        showToastWithTimeout("success", "✅ Success", "Successfully signed up. Please check your email for activation link.", 3000);
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        console.log("Signup failed, error message:", response.data.message);
        showToastWithTimeout("error", "Error", response.data.message, 5000);
      }
    } catch (error) {
      showToastWithTimeout("error", "Signup Failed", "There was an error signing up.", 3000);
    } finally {
      setIsSigningUp(false);
    }
  };

  const handleNextTab = () => {
    if (activeTab === "personal") {
      // Validate personal fields before moving to business tab
      const personalFields = ["username", "firstName", "lastName", "emailAddress", "phoneNumber", "city", "physicalAddress", "country", "password", "confirmPassword"];
      
      form.trigger(personalFields as any).then((isValid) => {
        if (isValid) {
          setActiveTab("business");
        } else {
          showToastWithTimeout("error", "Validation Error", "Please fill in all required personal information fields correctly", 3000);
        }
      });
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return "bg-red-500";
    if (passwordStrength < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  // Get brand logo icon
  const getBrandIcon = () => {
    // Check if brand has a custom logo URL first
    if (brand.logo.url) {
      return (
        <img
          alt={brand.name}
          src={brand.logo.url}
          className="h-16 w-auto object-contain"
        />
      );
    }
    
    if (brand.logo.icon === 'SparklesIcon') {
      return <Sparkles className="h-16 w-auto text-primary" />;
    } else if (brand.logo.icon === 'BoxIcon') {
      return <Box className="h-16 w-auto text-primary" />;
    }
    return (
      <img
        alt={brand.name}
        src={LogoWithSlogan}
        className="h-16 w-auto"
      />
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <motion.div 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex justify-center"
        >
          {getBrandIcon()}
        </motion.div>
        <motion.h2 
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-6 text-center text-3xl font-extrabold text-primary"
        >
          Create your account
        </motion.h2>
        <motion.p 
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-2 text-center text-sm text-gray-600"
        >
          {brand.tagline}
        </motion.p>
      </div>

      <motion.div 
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl"
      >
        <div className="bg-white py-8 px-4 shadow-xl sm:rounded-xl sm:px-10 border border-gray-100">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8">
                  <TabsTrigger value="personal" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Personal Information
                  </TabsTrigger>
                  <TabsTrigger value="business" className="flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Business Information
                  </TabsTrigger>
                </TabsList>
                
                <AnimatePresence mode="wait">
                  <TabsContent value="personal" className="mt-0">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-xl text-primary">Personal Details</CardTitle>
                          <CardDescription>
                            Enter your personal information to create your account
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <FormField
                              name="username"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Username <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <Input 
                                      {...field} 
                                      type="text" 
                                      placeholder="e.g. johndoe" 
                                      className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary" 
                                      required 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              name="emailAddress"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Email Address <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <Input 
                                      {...field} 
                                      type="email" 
                                      placeholder="e.g. johndoe@example.co.za" 
                                      className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary" 
                                      required 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              name="firstName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>First Name <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <Input 
                                      {...field} 
                                      type="text" 
                                      placeholder="e.g. John" 
                                      className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary" 
                                      required 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              name="lastName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Last Name <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <Input 
                                      {...field} 
                                      type="text" 
                                      placeholder="e.g. Doe" 
                                      className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary" 
                                      required 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              name="phoneNumber"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Phone Number <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <PhoneInput 
                                      {...field} 
                                      placeholder="e.g. +27 21 123 4567" 
                                      className="h-11 rounded-md border border-gray-300 focus:ring-primary focus:border-primary" 
                                      international 
                                      countryCallingCodeEditable={false}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              name="gender"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Gender</FormLabel>
                                  <FormControl>
                                    <Select {...field} onValueChange={(value) => field.onChange(value)}>
                                      <SelectTrigger className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary">
                                        <SelectValue placeholder="Select gender" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="male">Male</SelectItem>
                                        <SelectItem value="female">Female</SelectItem>
                                        <SelectItem value="other">Other</SelectItem>
                                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <Separator className="my-4" />
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <FormField
                              name="city"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>City <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <Input 
                                      {...field} 
                                      type="text" 
                                      placeholder="e.g. Cape Town" 
                                      className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary" 
                                      required 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              name="country"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Country <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <Select {...field} onValueChange={(value) => field.onChange(value)}>
                                      <SelectTrigger className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary">
                                        <SelectValue placeholder="Select country" />
                                      </SelectTrigger>
                                      <SelectContent className="max-h-[200px]">
                                        {countries.map((country) => (
                                          <SelectItem key={country} value={country}>
                                            {country}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              name="physicalAddress"
                              render={({ field }) => (
                                <FormItem className="col-span-1 sm:col-span-2">
                                  <FormLabel>Physical Address <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <Input 
                                      {...field} 
                                      type="text" 
                                      placeholder="e.g. 123 Main St, Claremont" 
                                      className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary" 
                                      required 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <Separator className="my-4" />
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <FormField
                              name="password"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Password <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input
                                        {...field}
                                        type={showPassword ? "text" : "password"}
                                        placeholder="At least 6 characters"
                                        required
                                        className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary pr-10"
                                      />
                                      <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={() => setShowPassword(!showPassword)}
                                      >
                                        {showPassword ? 
                                          <EyeOff className="h-5 w-5 text-gray-400" /> : 
                                          <Eye className="h-5 w-5 text-gray-400" />
                                        }
                                      </button>
                                    </div>
                                  </FormControl>
                                  {field.value && (
                                    <div className="mt-2">
                                      <div className="h-1 w-full bg-gray-200 rounded-full overflow-hidden">
                                        <div 
                                          className={`h-full ${getPasswordStrengthColor()}`} 
                                          style={{ width: `${passwordStrength}%` }}
                                        ></div>
                                      </div>
                                      <p className="text-xs mt-1 text-gray-500">
                                        {passwordStrength < 40 ? "Weak" : 
                                         passwordStrength < 70 ? "Medium" : "Strong"} password
                                      </p>
                                    </div>
                                  )}
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              name="confirmPassword"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Confirm Password <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input
                                        {...field}
                                        type={showConfirmPassword ? "text" : "password"}
                                        placeholder="Repeat your password"
                                        required
                                        className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary pr-10"
                                      />
                                      <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                      >
                                        {showConfirmPassword ? 
                                          <EyeOff className="h-5 w-5 text-gray-400" /> : 
                                          <Eye className="h-5 w-5 text-gray-400" />
                                        }
                                      </button>
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </CardContent>
                        <CardFooter className="flex justify-end">
                          <Button 
                            type="button" 
                            onClick={handleNextTab}
                            className="bg-primary hover:bg-primary-foreground text-white font-medium py-2 px-6 rounded-md flex items-center gap-2"
                          >
                            Next
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </CardFooter>
                      </Card>
                    </motion.div>
                  </TabsContent>
                  
                  <TabsContent value="business" className="mt-0">
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-xl text-primary">Business Details</CardTitle>
                          <CardDescription>
                            Tell us about your business to complete your account setup
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <FormField
                              name="businessName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Business Name <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <Input 
                                      {...field} 
                                      type="text" 
                                      placeholder="e.g. Doe Enterprises" 
                                      className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary" 
                                      required 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              name="businessType"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Business Type <span className="text-red-500">*</span></FormLabel>
                                  <Select {...field} onValueChange={(value) => field.onChange(value)}>
                                    <SelectTrigger className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary">
                                      <SelectValue placeholder="Select business type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem>
                                      <SelectItem value="partnership">Partnership</SelectItem>
                                      <SelectItem value="corporation">Corporation</SelectItem>
                                      <SelectItem value="limited_liability_company">Private Limited Company</SelectItem>
                                      <SelectItem value="nonprofit">Non-profit</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              name="businessEmail"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Business Email</FormLabel>
                                  <FormControl>
                                    <Input 
                                      {...field} 
                                      type="email" 
                                      placeholder="e.g. business@example.co.za" 
                                      className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary" 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              name="businessPhoneNumber"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Business Phone Number</FormLabel>
                                  <FormControl>
                                    <PhoneInput 
                                      {...field} 
                                      placeholder="e.g. +27 21 123 4567" 
                                      className="h-11 rounded-md border border-gray-300 focus:ring-primary focus:border-primary" 
                                      international 
                                      countryCallingCodeEditable={false}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <Separator className="my-4" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <FormField
                              name="businessAddress"
                              render={({ field }) => (
                                <FormItem className="col-span-1 sm:col-span-2">
                                  <FormLabel>Business Address <span className="text-red-500">*</span></FormLabel>
                                  <FormControl>
                                    <Input 
                                      {...field} 
                                      type="text" 
                                      placeholder="e.g. 456 Business Rd, Cape Town" 
                                      className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary"
                                      required
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                              <FormField
                                name="businessCity"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>City <span className="text-red-500">*</span></FormLabel>
                                    <FormControl>
                                      <Input 
                                        {...field} 
                                        type="text" 
                                        placeholder="e.g. Johannesburg" 
                                        className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary"
                                        required
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              
                              <FormField
                                name="businessState"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Province/State</FormLabel>
                                    <FormControl>
                                      <Input 
                                        {...field} 
                                        type="text" 
                                        placeholder="e.g. Gauteng" 
                                        className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                              <FormField
                                name="businessCountry"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Country <span className="text-red-500">*</span></FormLabel>
                                    <FormControl>
                                      <Select 
                                        value={field.value} 
                                        onValueChange={(value) => field.onChange(value)}
                                      >
                                        <SelectTrigger className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary">
                                          <SelectValue placeholder="Select country" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[200px]">
                                          {countries.map((country) => (
                                            <SelectItem key={country} value={country}>
                                              {country}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              
                              <FormField
                                name="businessPostalCode"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Postal Code</FormLabel>
                                    <FormControl>
                                      <Input 
                                        {...field} 
                                        type="text" 
                                        placeholder="e.g. 8001" 
                                        className="h-11 rounded-md border-gray-300 focus:ring-primary focus:border-primary"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            
                            <FormField
                              name="businessDescription"
                              render={({ field }) => (
                                <FormItem className="col-span-1 sm:col-span-2">
                                  <FormLabel>Business Description</FormLabel>
                                  <FormControl>
                                    <Textarea 
                                      {...field} 
                                      placeholder="Describe what your business does" 
                                      className="min-h-[100px] rounded-md border-gray-300 focus:ring-primary focus:border-primary"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              name="businessWebsite"
                              render={({ field }) => (
                                <FormItem className="col-span-1 sm:col-span-2">
                                  <FormLabel>Business Website</FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <Globe className="h-5 w-5 text-gray-400" />
                                      </div>
                                      <Input 
                                        {...field} 
                                        type="url" 
                                        placeholder="e.g. www.business.co.za" 
                                        className="h-11 pl-10 rounded-md border-gray-300 focus:ring-primary focus:border-primary"
                                      />
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          <Separator className="my-6" />
                          
                          <div className="space-y-4">
                            <h3 className="text-lg font-medium text-primary">Business Branding</h3>
                            <p className="text-sm text-gray-500">Upload your business logo and banner to personalize your account</p>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                              <FormField
                                name="businessLogoImg"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      <div className="flex items-center gap-2">
                                        <Image className="h-4 w-4 text-primary" />
                                        Business Logo
                                      </div>
                                    </FormLabel>
                                    <FormControl>
                                      <div className="flex items-center justify-center w-full">
                                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            <Upload className="w-8 h-8 mb-3 text-gray-400" />
                                            <p className="mb-2 text-sm text-gray-500">
                                              <span className="font-semibold">Click to upload</span> or drag and drop
                                            </p>
                                            <p className="text-xs text-gray-500">SVG, PNG, JPG or GIF (max. 2MB)</p>
                                          </div>
                                          <Input 
                                            {...field} 
                                            id="businessLogoImg"
                                            type="file" 
                                            className="hidden"
                                            onChange={(e) => handleUpload("businessLogoImg", e)} 
                                          />
                                        </label>
                                      </div>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              
                              <FormField
                                name="businessBannerImg"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      <div className="flex items-center gap-2">
                                        <ImageIcon className="h-4 w-4 text-primary" />
                                        Business Banner
                                      </div>
                                    </FormLabel>
                                    <FormControl>
                                      <div className="flex items-center justify-center w-full">
                                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            <Upload className="w-8 h-8 mb-3 text-gray-400" />
                                            <p className="mb-2 text-sm text-gray-500">
                                              <span className="font-semibold">Click to upload</span> or drag and drop
                                            </p>
                                            <p className="text-xs text-gray-500">SVG, PNG, JPG or GIF (max. 2MB)</p>
                                          </div>
                                          <Input 
                                            {...field} 
                                            id="businessBannerImg"
                                            type="file" 
                                            className="hidden"
                                            onChange={(e) => handleUpload("businessBannerImg", e)} 
                                          />
                                        </label>
                                      </div>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                          
                          <div className="mt-8">
                            <Button 
                              type="submit" 
                              className="w-full h-12 bg-primary hover:bg-primary-foreground text-white font-medium rounded-md shadow-sm transition-all duration-200 flex items-center justify-center gap-2"
                            >
                              {isSigningUp ? (
                                <>
                                  <Loader2 className="w-5 h-5 animate-spin" />
                                  <span>Creating your account...</span>
                                  {progress > 0 && (
                                    <div className="absolute bottom-0 left-0 h-1 bg-blue-300 rounded-b-md" style={{ width: `${progress}%` }} />
                                  )}
                                </>
                              ) : (
                                <>
                                  <Check className="w-5 h-5" />
                                  <span>Complete Registration</span>
                                </>
                              )}
                            </Button>
                          </div>
                          
                          <div className="relative mt-10">
                            <div aria-hidden="true" className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-gray-200" />
                            </div>
                            <div className="relative flex justify-center text-sm font-medium">
                              <span className="bg-white px-6 text-gray-500">Or continue with</span>
                            </div>
                          </div>
                          
                          <div className="mt-6 grid grid-cols-1 gap-4">
                            <Button 
                              type="button"
                              variant="outline"
                              className="w-full h-12 flex items-center justify-center gap-3 border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 transition-all duration-200"
                              disabled
                            >
                              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                                <path
                                  d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                                  fill="#EA4335"
                                />
                                <path
                                  d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                                  fill="#4285F4"
                                />
                                <path
                                  d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                                  fill="#FBBC05"
                                />
                                <path
                                  d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                                  fill="#34A853"
                                />
                              </svg>
                              <span className="font-medium">Sign up with Google</span>
                              <span className="ml-auto text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Coming soon</span>
                            </Button>
                          </div>
                          
                          <p className="mt-8 text-center text-sm text-gray-500">
                            Already have an account?{' '}
                            <a href="/login" className="font-medium text-primary hover:text-primary-foreground transition-colors">
                              Sign in instead
                            </a>
                          </p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </TabsContent>
                </AnimatePresence>
              </Tabs>
            </form>
          </Form>
        </div>
      </motion.div>
    </div>
  );
}